import { Injectable } from '@angular/core';
import { lastValueFrom, map, Observable } from 'rxjs';
import { Constants } from '../../../modules/constants';
import { LandingService } from '../../../modules/landing/landing.service';
import { IProduct } from '../../../modules/product/product-models';
import { ProductService } from '../../../modules/product/product.service';
import { UrlShortenerService } from '../../../modules/social/url/url-shortener.service';
import { IShortUrl } from '../../../modules/social/url/url-shortener-models';
import { GqlPwpProfileService } from '../../pwp-section/gql-pwp-profile.service';
import { FloatingPresence } from '../floating-presence/floating-presence-models';

@Injectable({ providedIn: 'root' })
export class FloatingPresenceService {
    countryCode = '';
    private activePwpSite: string | null = null;
    // Memoize the in-flight or resolved getPwpProfile call so multiple
    // subscribers share one network call.
    private cachedPwpSite: string | null = null;
    private cachedProfile: Promise<FloatingPresence> | null = null;

    constructor(
        private readonly gqlPwpProfileService: GqlPwpProfileService,
        private readonly landingService: LandingService,
        private readonly productService: ProductService,
        private readonly urlShortenerService: UrlShortenerService,
    ) {}

    setActivePwpSite(site: string | null): void {
        this.activePwpSite = site;
    }

    getActivePwpSite(): string | null {
        return this.activePwpSite;
    }

    // True when a sponsor's PWP profile is active for this session.
    hasSponsor(): boolean {
        return !!this.activePwpSite;
    }

    loadFloatingPresence(): Promise<FloatingPresence> {
        if (!this.activePwpSite) {
            return Promise.resolve(new FloatingPresence());
        }
        if (this.cachedProfile && this.cachedPwpSite === this.activePwpSite) {
            return this.cachedProfile;
        }
        this.cachedPwpSite = this.activePwpSite;
        this.cachedProfile = lastValueFrom(
            this.gqlPwpProfileService.getPwpProfile(this.activePwpSite),
        ).then((fp) => {
            this.countryCode = fp.country;
            return fp;
        }).catch((err) => {
            this.cachedPwpSite = null;
            this.cachedProfile = null;
            throw err;
        });
        return this.cachedProfile;
    }

    getEnrollLink(): Observable<string> {
        return this.urlShortenerService.buildShortUrl(
            Constants.SHARE_TYPE_MARKETING_PAGE,
            Constants.SHARE_NAME_ASSOCIATE_SIGNUP,
            Constants.SHARE_FROM_PWP,
            'pfp',
        ).pipe(
            map((shortUrl: IShortUrl) => shortUrl.url),
        );
    }

    loadFeaturedProducts(): Promise<IProduct[]> {
        if (!this.activePwpSite) {
            return Promise.resolve([]);
        }
        return lastValueFrom(this.landingService.getLandingProducts());
    }

    loadProduct(usanaId: string): Promise<any> {
        if (!this.activePwpSite) {
            return Promise.resolve({});
        }
        return lastValueFrom(this.productService.getProductGroupByVariant(usanaId));
    }
}
